import { inject, injectable, interfaces } from 'inversify';
import { action, makeObservable, observable } from 'mobx';
import { IAlertBarStore } from '../../../modules/alertBar/AlertBar.store';
import { Alert } from '../../../modules/alertBar/models/Alert';
import { IOverlayLoaderStore } from '../../../modules/common/OverlayLoader.store';
import { ICurrentWorkspaceStore } from '../../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { CurrentWorkspaceChangedEventType } from '../../../modules/workspaces/currentWorkspace/events/CurrentWorkspaceChangedEvent';
import { ACDLError, UnauthorizedError } from '../../models/error.model';
import { ProjectRole } from '../../models/userRole.model';
import { IMainApiService, IPagedResult } from '../../services/api.service';
import { IApiService } from '../../services/api.service.base';
import { EventListeningDisposer, IEventBus } from '../../services/eventBus.service';
import { IBillingStore } from '../billing/billing.store';
import { NotificationLevel, ToastNotification } from '../notifications/models/notification.model';
import { INotificationsService } from '../notifications/services/notifications.service';
import { UserRemovedFromProjectType } from '../projectDetails/events/userRemovedFromProject';
import { EncryptionMode, IWorkspacesStore, WorkspaceRole } from '../workspaces/workspaces.store';
import { UserInfoLoadedEvent } from './events/userInfoLoadedEvent';
import { UserSettingsLoadedEvent } from './events/userSettingsLoadedEvent';
import { IAdminUserInfo, IPlanInfo, IProjectRole, IUserStore, ListViewMode, Plan, UserRole } from './user.store';

interface IUpdateUserDetailsPayload {
  fullName: string;
  company: string;
  country: string;
}

interface IUserSettingsPayload {
  workspaceId: string;
  projectImagesAsList: boolean;
  datasetImagesAsList: boolean;
  attachmentsAsList: boolean;
  projectsAsList: boolean;
}

interface IUserInfoPayload {
  id: string;
  email: string;
  fullName: string;
  company: string;
  country: string;
  phoneNumber: string;
  role: UserRole;
  showCart: boolean;
  canAddWorkspace: boolean;
  workspaces: IWorkspacePayload[];
  projectRoles: IProjectRole[];
  alerts: Alert[];
}

interface IWorkspacePayload {
  name: string;
  id: string;
  role: WorkspaceRole;
  locked: boolean;
  timeZoneOffset: number;
  ownerEmail: string;
  ownerPlanTier: Plan;
  encryption: EncryptionMode;
  ownerPlanInfo: {
    plan: Plan;
    limits: {
      maxWorkspaces: number;
      maxWorkspaceUsers: number;
      maxStorageInGB: number;
    };
    usedLimits: {
      usedWorkspaces: number;
      usedWorkspaceUsers: number;
      usedStorageInGB: number;
      workspaceLimitReached: boolean;
      workspaceUsersLimitReached: boolean;
      availableSpaceLimitReached: boolean;
    };
    planExpirationDate: string | null;
    dataRetentionDate: string | null;
  };
}

export interface IUserService {
  adminConfirmUser(id: string): Promise<void | ACDLError>;
  adminChangePassword(userId: string, NewPassword: string): Promise<void | ACDLError>;
  getUserInfoAsync(): Promise<void | ACDLError>;
  getUserSettingsAsync(): Promise<void | ACDLError>;
  getUsers(search: string): Promise<void | ACDLError>;
  updateUserDetails(fullName: string, company: string, country: string): Promise<void | ACDLError>;
  getPlansInfoAsync(pageNumber: number, pageSize: number, orderBy?: string, orderType?: string, search?: string): Promise<void | ACDLError>;
  changeUserPasswordAsync(oldPassword: string, newPassword: string): Promise<void | ACDLError>;
  deleteUserAsync(): Promise<void | ACDLError>;
  changeUserRoleAsync(id: string, role: UserRole): Promise<void | ACDLError>;
  changeUserPlanAsync(id: string, planInfo: IPlanInfo): Promise<void | ACDLError>;
  getPlanDefaultsAsync(): Promise<void | ACDLError>;
  lockUserAsync(userId: string, locked: boolean): Promise<void | ACDLError>;
  setProjectImageListViewMode(mode: ListViewMode): Promise<void>;
  setDatasetImageListViewMode(mode: ListViewMode): Promise<void>;
  setAttachmentsListViewMode(mode: ListViewMode): Promise<void>;
  setProjectsListViewMode(mode: ListViewMode): Promise<void>;
  getUserRoleInProject(projectId: string): ProjectRole | undefined;
  isInRole(projectId: string | undefined, projectRoles: ProjectRole[]): boolean;
  changeEmail(password: string, newEmail: string): Promise<void | ACDLError>;
  userListPaginationChange(pageNumber: number, pageSize: number, search: string): Promise<void>;
  userListOrderChange(orderBy: string, orderType: string, search: string): Promise<void>;
  userListSearchChange(search: string): Promise<void>;
  initSearch(): void;
  data: IUserStore;
  billing: IBillingStore;
}

export namespace IUserService {
  export const $: interfaces.ServiceIdentifier<IUserService> = Symbol('USER_SERVICE');
}

@injectable()
export class UserService implements IUserService {
  private disposers: EventListeningDisposer[];

  constructor(
    @inject(IUserStore.$) public readonly data: IUserStore,
    @inject(IAlertBarStore.$) public readonly alertBarStoreSetter: IAlertBarStore,
    @inject(IWorkspacesStore.$) public readonly workspaceStore: IWorkspacesStore,
    @inject(ICurrentWorkspaceStore.$) private readonly currentWorkspaceStore: ICurrentWorkspaceStore,
    @inject(IBillingStore.$) public readonly billing: IBillingStore,
    @inject(IMainApiService.$) private readonly apiService: IApiService,
    @inject(INotificationsService.$) private readonly notificationsService: INotificationsService,
    @inject(IEventBus.$) private readonly eventBus: IEventBus,
    @inject(IOverlayLoaderStore.$) private readonly overlayLoaderStore: IOverlayLoaderStore
  ) {
    makeObservable(this);
    this.disposers = [
      this.eventBus.addListener(this.currentWorkspaceChangedListener, CurrentWorkspaceChangedEventType),
      this.eventBus.addListener(this.userRemovedFromProjectListener, UserRemovedFromProjectType),
    ];
  }

  async adminConfirmUser(id: string): Promise<void | ACDLError> {
    const url = '/Users/AdminConfirmUser';
    const result = await this.apiService.postAsync(url, { id });
    if (result instanceof ACDLError) {
      if (!(result instanceof UnauthorizedError)) {
        await this.notificationsService.push(new ToastNotification(NotificationLevel.ERROR, 'user_confirmation_failed'));
      }
    } else {
      const userIndex = this.data.adminUserInfo.findIndex((u) => u.id === id);

      if (userIndex !== -1) {
        const newArray = this.data.adminUserInfo.slice();
        newArray[userIndex].isEmailConfirmed = true;
        this.data.adminUserInfo.replace(newArray);
      }

      await this.notificationsService.push(new ToastNotification(NotificationLevel.SUCCESS, 'user_confirmed_successfully'));
    }
  }

  async adminChangePassword(userId: string, newPassword: string): Promise<void | ACDLError> {
    const url = '/Users/UpdatePassword';
    const result = await this.apiService.postAsync(url, { userId, newPassword });

    if (result instanceof ACDLError) {
      if (!(result instanceof UnauthorizedError)) {
        await this.notificationsService.push(new ToastNotification(NotificationLevel.ERROR, 'user_password_change_failed'));
      }
      return;
    }
    void this.notificationsService.push(new ToastNotification(NotificationLevel.SUCCESS, 'password_changed_successfully'));
  }

  initSearch(): void {
    this.data.adminUsersPaging = { ...this.data.adminUsersPaging, search: '' };
  }

  async userListPaginationChange(pageNumber: number, pageSize: number, search: string): Promise<void> {
    this.data.adminUsersPaging = { ...this.data.adminUsersPaging, pageNumber, pageSize };
    await this.getUsers(search);
  }

  async userListOrderChange(orderBy: string, orderType: string, search: string): Promise<void> {
    this.data.adminUsersPaging = { ...this.data.adminUsersPaging, orderBy, orderType };
    await this.getUsers(search);
  }

  async userListSearchChange(search: string): Promise<void> {
    this.data.adminUsersPaging = { ...this.data.adminUsersPaging, search, pageNumber: 1 };

    await this.overlayLoaderStore.withLoaderAsync('users-list-table', async (): Promise<void> => {
      await this.getUsers(search);
    });
  }

  cleanup(): void {
    this.disposers.forEach((d) => {
      d();
    });
  }

  @action.bound
  async getUserInfoAsync(): Promise<void | ACDLError> {
    const url = '/Users/UserInfo';
    const result = await this.apiService.getAsync<IUserInfoPayload>(url);
    if (result instanceof ACDLError) {
      if (!(result instanceof UnauthorizedError)) {
        await this.notificationsService.push(new ToastNotification(NotificationLevel.ERROR, 'get_user_info_failed'));
      }
      return;
    }

    const myPlanInfo = result.workspaces.find((w) => w.ownerEmail === result.email)?.ownerPlanInfo;

    if (!myPlanInfo) {
      throw new Error('User plan info is missing');
    }

    this.data.userInfo = {
      id: result.id,
      email: result.email,
      fullName: result.fullName,
      company: result.company,
      country: result.country,
      phoneNumber: result.phoneNumber,
      role: result.role,
      showCart: result.showCart,
      emailChangePending: false,
      projectRoles: result.projectRoles,
    };
    this.data.planInfo = {
      plan: myPlanInfo.plan,
      customLimits: false,
      isActive: true,
      availableSpace: myPlanInfo.limits.maxStorageInGB,
      workspaces: myPlanInfo.limits.maxWorkspaces,
      workspaceUsers: myPlanInfo.limits.maxWorkspaceUsers,
      dataRetentionDate: myPlanInfo.dataRetentionDate,
      planExpirationDate: myPlanInfo.planExpirationDate,
    };
    this.billing.usedSpace = myPlanInfo.usedLimits.usedStorageInGB * 1024;
    this.billing.workspaces = myPlanInfo.usedLimits.usedWorkspaces;
    this.billing.workspaceUsers = myPlanInfo.usedLimits.usedWorkspaceUsers;
    this.workspaceStore.workspaces.replace(result.workspaces);
    this.workspaceStore.canAddWorkspace = result.canAddWorkspace;
    this.alertBarStoreSetter.setAlerts(result.alerts);

    this.eventBus.sendEvent(new UserInfoLoadedEvent());
  }

  @action
  async getUserSettingsAsync(): Promise<void | ACDLError> {
    const url = '/Users/UserSettings';
    const result = await this.apiService.getAsync<IUserSettingsPayload>(url);
    if (result instanceof ACDLError) {
      if (!(result instanceof UnauthorizedError)) {
        await this.notificationsService.push(new ToastNotification(NotificationLevel.ERROR, 'get_user_settings_failed'));
      }
      return;
    }

    if (result.workspaceId) {
      this.workspaceStore.setPreferredWorkspace(result.workspaceId);
    }
    this.data.projectImageListViewMode = result.projectImagesAsList ? ListViewMode.List : ListViewMode.Tiles;
    this.data.datasetImageListViewMode = result.datasetImagesAsList ? ListViewMode.List : ListViewMode.Tiles;
    this.data.attachmentsListViewMode = result.attachmentsAsList ? ListViewMode.List : ListViewMode.Tiles;
    this.data.projectListViewMode = result.projectsAsList ? ListViewMode.List : ListViewMode.Tiles;

    this.eventBus.sendEvent(new UserSettingsLoadedEvent());
  }

  @action
  async getUsers(search: string | undefined): Promise<void | ACDLError> {
    const { pageNumber, pageSize, orderBy, orderType } = this.data.adminUsersPaging;
    const url = `/Users/GetUsers?pageNumber=${pageNumber}&pageSize=${pageSize}&orderBy=${orderBy}&orderType=${orderType}&search=${search ?? ''}`;
    const result = await this.apiService.getAsync<IPagedResult<IAdminUserInfo>>(url);
    if (result instanceof ACDLError) {
      await this.notificationsService.push(new ToastNotification(NotificationLevel.ERROR, 'get_user_admin_info_failed'));
      return;
    }

    this.data.adminUserInfo.replace(result.data);
    this.data.adminUsersPaging = {
      ...this.data.adminUsersPaging,
      totalCount: result.totalCount,
    };
  }

  @action
  async getPlansInfoAsync(pageNumber: number, pageSize: number, orderBy?: string, orderType?: string, search?: string): Promise<void | ACDLError> {
    const url = `/Users/GetPlans?pageNumber=${pageNumber}&pageSize=${pageSize}&orderBy=${orderBy}&orderType=${orderType}&search=${search}`;
    const result = await this.apiService.getAsync<IPagedResult<IPlanInfo>>(url);
    if (result instanceof ACDLError) {
      await this.notificationsService.push(new ToastNotification(NotificationLevel.ERROR, 'get_plans_info_failed'));
      return;
    }

    this.data.plansPaging = {
      search,
      pageSize,
      orderBy: orderBy || '',
      orderType: orderType || '',
      pageNumber: result.pageNumber,
      pagesCount: result.pagesCount,
      totalCount: result.totalCount,
    };

    this.data.plansInfo = observable.array(result.data, { deep: true });
  }

  @action
  async updateUserDetails(fullName: string, company: string, country: string): Promise<void> {
    const url = '/Users/UpdateDetails';
    const result = await this.apiService.postAsync<IUpdateUserDetailsPayload>(url, { fullName, company, country });

    if (!(result instanceof ACDLError)) {
      this.data.userInfo.fullName = fullName;
      this.data.userInfo.company = company;
      this.data.userInfo.country = country;
    }
  }

  @action
  async changeUserPasswordAsync(oldPassword: string, newPassword: string): Promise<void | ACDLError> {
    const url = '/Authorization/ChangePassword';
    return await this.apiService.postAsync(url, { oldPassword, newPassword });
  }

  @action
  async deleteUserAsync(): Promise<void | ACDLError> {
    const url = '/Authorization/DeleteUser';
    const result = await this.apiService.postAsync(url);
    if (result instanceof ACDLError) {
      await this.notificationsService.push(new ToastNotification(NotificationLevel.ERROR, 'delete_user_failed'));
    }
    return result;
  }

  @action
  async changeUserRoleAsync(id: string, role: UserRole): Promise<void> {
    const url = '/Users/changeRole';
    const result = await this.apiService.postAsync<{ id: string; role: string }>(url, { id, role });

    if (result instanceof ACDLError) {
      await this.notificationsService.push(new ToastNotification(NotificationLevel.ERROR, 'user_role_change_failed'));
      return;
    }

    const userIndex = this.data.adminUserInfo.findIndex((u) => u.id === id);

    if (userIndex !== -1) {
      const newArray = this.data.adminUserInfo.slice();
      newArray[userIndex].role = role;
      if (role === UserRole.User) {
        newArray[userIndex].plan = Plan.Free;
        newArray[userIndex].customLimits = false;
      }
      this.data.adminUserInfo.replace(newArray);
    }

    await this.notificationsService.push(new ToastNotification(NotificationLevel.SUCCESS, 'user_role_changed_successfully'));
  }

  @action
  async changeUserPlanAsync(id: string, plan: IPlanInfo): Promise<void | ACDLError> {
    const url = '/Users/ChangePlan';
    const result = await this.apiService.postAsync(url, plan);

    if (result instanceof ACDLError) {
      await this.notificationsService.push(new ToastNotification(NotificationLevel.ERROR, 'users_plan_changeing_failed'));
      return;
    }

    const userIndex = this.data.adminUserInfo.findIndex((u) => u.id === id);

    if (userIndex !== -1) {
      const newArray = this.data.adminUserInfo.slice();
      Object.assign(newArray[userIndex], plan);
      this.data.adminUserInfo.replace(newArray);
    }

    await this.notificationsService.push(new ToastNotification(NotificationLevel.SUCCESS, 'users_plan_changed_successfully'));
  }

  @action
  async getPlanDefaultsAsync(): Promise<void | ACDLError> {
    const url = '/Users/PlanDefaults';
    const result = await this.apiService.getAsync<IPlanInfo[]>(url);

    if (!(result instanceof ACDLError)) {
      this.data.planDefaults.replace(result);
    }
  }

  @action
  async lockUserAsync(userId: string, locked: boolean): Promise<void | ACDLError> {
    const url = '/Users/Lock';
    const result = await this.apiService.postAsync(url, { userId, locked });

    if (result instanceof ACDLError) {
      await this.notificationsService.push(new ToastNotification(NotificationLevel.ERROR, locked ? 'user_locking_failed' : 'user_unlocking_failed'));
      return;
    }

    const userIndex = this.data.adminUserInfo.findIndex((u) => u.id === userId);

    if (userIndex !== -1) {
      const newArray = this.data.adminUserInfo.slice();
      newArray[userIndex].isActive = !locked;
      this.data.adminUserInfo.replace(newArray);
    }

    await this.notificationsService.push(new ToastNotification(NotificationLevel.SUCCESS, locked ? 'user_locked_successfully' : 'user_unlocked_successfully'));
  }

  @action
  async updateUserSettings(): Promise<void | ACDLError> {
    const url = '/Users/UpdateUserSettings';
    await this.apiService.postAsync<IUserSettingsPayload>(url, {
      workspaceId: this.currentWorkspaceStore.currentWorkspace.id,
      projectImagesAsList: this.data.projectImageListViewMode === ListViewMode.List,
      datasetImagesAsList: this.data.datasetImageListViewMode === ListViewMode.List,
      attachmentsAsList: this.data.attachmentsListViewMode === ListViewMode.List,
      projectsAsList: this.data.projectListViewMode === ListViewMode.List,
    });
  }

  @action
  async setProjectImageListViewMode(mode: ListViewMode): Promise<void> {
    this.data.projectImageListViewMode = mode;
    await this.updateUserSettings();
  }

  @action
  async setDatasetImageListViewMode(mode: ListViewMode): Promise<void> {
    this.data.datasetImageListViewMode = mode;
    await this.updateUserSettings();
  }

  @action
  async setAttachmentsListViewMode(mode: ListViewMode): Promise<void> {
    this.data.attachmentsListViewMode = mode;
    await this.updateUserSettings();
  }

  async setProjectsListViewMode(mode: ListViewMode): Promise<void> {
    this.data.projectListViewMode = mode;
    await this.updateUserSettings();
  }

  getUserRoleInProject(projectId: string): ProjectRole | undefined {
    const projectRole = this.data.userInfo.projectRoles.find((p) => p.projectId === projectId);
    return projectRole && projectRole.role;
  }

  isInRole(projectId: string | undefined, projectRoles: ProjectRole[]): boolean {
    if (!projectId) {
      return false;
    }
    const userRoleInProject = this.getUserRoleInProject(projectId);
    return !!userRoleInProject && projectRoles.includes(userRoleInProject);
  }

  async changeEmail(password: string, email: string): Promise<void | ACDLError> {
    const url = '/Authorization/RequestChangeEmail';
    const result = await this.apiService.postAsync(url, { password, email });

    if (!(result instanceof ACDLError)) {
      this.data.userInfo.emailChangePending = true;
    }
  }

  private currentWorkspaceChangedListener = async (): Promise<void> => {
    if (this.workspaceStore.preferredWorkspaceId === this.currentWorkspaceStore.currentWorkspace.id) {
      return;
    }
    await this.updateUserSettings();
  };

  private userRemovedFromProjectListener = async (): Promise<void | ACDLError> => this.getUserInfoAsync();
}
